<template lang="pug">
.schedules
  SchedulesFilters(
    :class="{ loading }"
    :more-users-to-load="moreUsersToLoad"
    @filter-change="updateFilters"
    @open-dialog="openDialog"
  )
  #schedules-fullcalendar.fullcalendar-wrapper.mt-15(:class="{ loading }")
    .d-flex.align-items-center.justify-content-between.flex-sm-row.flex-column
      .d-flex.gap-15
        .sm-hide
          SchedulesFiltersViewToggles(@filter-change="updateFilters")
        SchedulesDropdown(
          v-if="onWeekView"
          label="Group by"
          :options="weekViewOptions"
          :initial-value="subtype"
          param-name="subtype"
          @filter-change="updateFilters"
        )

      .d-flex.align-items-center.max-members-warning.text-muted.gap-5(v-if="moreUsersToLoad && !onMembersView")
        i.hi.hi-12.hi-info-filled
        | A max of 20 members are shown in this view.

      template(v-if="onMembersView")
        label.unscheduled-toggle-wrapper.toggle-switch-wrapper.mt-sm-0.mt-10
          input.custom-control-input(
            type="checkbox"
            :checked="unscheduledToggle"
            @change="showUnscheduled"
          )
          .toggle-switch-label.text-transform-none Show unscheduled
          .toggle-switch

    .infinite-scroll.w-full(
      v-if="hasMembersFilter"
      ref="scroller"
    )
      FullCalendar(
        v-if="fullcalendarLoaded"
        :key="calendarKey"
        :options="{ ...calendarOptions, events: allEvents }"
      )
        //- Event-content components render inside @fullcalendar/vue3's own mini `createApp`, which
        //- does NOT inherit this app's context (no $page/globalProperties — the lib has a literal
        //- "TODO: do something with appContext"). Pass what they need as props from this host.
        template(#eventContent="{ event }")
          component(
            :is="eventComponent"
            :event="event"
            :time-zone="time_zone.for"
          )
        template(
          v-if="!onDayView"
          #dayHeaderContent="{ date }"
        )
          template(v-if="onWeekView")
            .fc-cell-content
              .scheduler-heading-date
                .scheduler-heading-date-day-of-month(data-testid="date-day-of-month") {{ formattedDay(date) }}
                .day-month-wrapper
                  .scheduler-heading-date-day-of-week {{ formattedDayName(date) }}
                  .scheduler-heading-date-month-name {{ formattedMonth(date) }}
          span(v-else-if="onMonthView") {{ formattedDayName(date) }}

        template(
          v-if="onMembersView"
          #slotLabelContent="{ date }"
        )
          .fc-cell-content
            .scheduler-heading-date
              .scheduler-heading-date-day-of-month(data-testid="date-day-of-month") {{ formattedDay(date) }}
              .day-month-wrapper
                .scheduler-heading-date-day-of-week {{ formattedDayName(date) }}
                .scheduler-heading-date-month-name {{ formattedMonth(date) }}

        template(#resourceAreaHeaderContent)

        template(
          v-if="onMembersView"
          #resourceLabelContent="event"
        )
          a(
            :href="`/organizations/${organization.id}/members/${event.resource.id}?tab=work%20hours`"
            target="_blank"
          )
            img.avatar.avatar-speck(:src="event.resource.extendedProps.avatar")
            span.member-name {{ event.fieldValue }}

      .text-center
        button.load-more-link.btn.btn-outline-primary.btn-sm(
          ref="loadMoreTrigger"
          :aria-disabled="loading"
          :disabled="loading"
          :style="{ opacity: loading ? 0.5 : 1, visibility: showMoreLink ? 'visible' : 'hidden' }"
          @click.prevent="loadMoreData()"
        )
          | {{ loading ? 'Loading...' : 'Load more' }}

    .empty-results.d-flex.flex-column.align-items-center.justify-content-center.height.p-40(
      v-if="!loading && (!hasMembersFilter || !users.length)"
    )
      img(
        :src="IMAGE_EMPTY_BOX"
        alt="No members selected; to view calendar data, please select members or teams"
      )
      .d-flex.flex-column.align-items-center.gap-5
        h6.font-normal.m-0 No members selected
        p.text-muted To view calendar data, please select&nbsp;
          template(v-if="!hasTeamsFilter && !hasMembersFilter")
            | teams or members
          template(v-else-if="hasTeamsFilter && !hasMembersFilter")
            | teams and members
          template(v-else)
            | members

    ScheduleDialog#new-schedule-form(
      v-if="showNewScheduleDialog"
      key="create-schedule-dialog"
      :date-range="scheduleDateRange"
      :editing-attendance-schedule="{ user_id: selectedUserId, filteredUsers: users }"
      @close="closeScheduleDialog()"
    )

    HolidayWizard(
      v-if="showHolidayWizardDialog"
      :show="showHolidayWizardDialog"
      :organization-id="organization.id"
      :organization-tz="organization.time_zone.for"
      :redirect-path="redirectPath"
      :country-list="country_list"
      @close="toggleScheduleDialog('showHolidayWizardDialog', false)"
      @loading="setLoading"
    )

    TimeOffRequestSetupDialog(
      v-if="showTimeOffDialog"
      :show="showTimeOffDialog"
      :allow-manage="user_permissions.time_off_requests.manage"
      :organization-id="organization.id"
      :redirect-path="redirectPath"
      :current-user-dropdown-object="current_user_dropdown_object"
      @close="toggleScheduleDialog('showTimeOffDialog', false)"
      @loading="setLoading"
    )

    AttendanceShiftDialog(
      v-if="showAttendanceShiftDialog"
      :event="currentEvent"
      @close="closeDialogs"
    )

    RemoveMultipleAttendanceSchedulesDialog(
      v-if="showDeleteSchedulesDialog"
      key="remove-schedules-dialog"
      :filtered-users="users"
      @close="toggleScheduleDialog('showDeleteSchedulesDialog', false)"
    )

    TimeOffRequestActionDialog(
      v-if="showTimeOffRequestDialog"
      :organization-id="organization.id"
      :selected-time-off-request-id="currentEvent?.timeOffRequestAttributes?.id"
      :redirect-path="redirectPath"
      show-original-time-zone
      show
      @close="closeDialogs"
    )

    JobDialog(
      v-if="showJobDialog"
      :event="currentEvent"
      @close="closeDialogs"
    )

    HolidayDialog(
      v-if="showHolidayDialog"
      :event="currentEvent"
      @close="closeDialogs"
    )

    IcalendarDialog#icalendar-dialog(
      v-if="showIcalendarDialog"
      @close="toggleScheduleDialog('showIcalendarDialog', false)"
    )
</template>

<script>
// This is so that `vite dev` can work as expected in dev mode.
// See: https://github.com/fullcalendar/fullcalendar-vue/issues/152#issue-918047023
//
// TODO: Remove when moving to v6
import '@fullcalendar/core/vdom';
import { toRaw } from 'vue';
import FullCalendar from '@fullcalendar/vue3';
import SchedulesFilters from '~/components/attendance_schedules/SchedulesFilters.vue';

import { IMAGE_EMPTY_BOX } from '~/rails_assets';
import { InertiaFlashWatcher, InertiaLoadingWatcher, mapInertiaProps } from '~/utils/inertia';
import HolidayDialog from '~/components/attendance_schedules/dialogs/HolidayDialog.vue';
import AttendanceShiftDialog from '~/components/attendance_schedules/dialogs/AttendanceShiftDialog.vue';
import DayViewEvent from '~/components/attendance_schedules/events/DayViewEvent.vue';
import HolidayWizard from '~/components/holidays/HolidayWizard.vue';
import TimeOffRequestSetupDialog from '~/components/time_off/TimeOffRequestSetupDialog.vue';
import TimeOffRequestActionDialog from '~/components/time_off/TimeOffRequestActionDialog.vue';
import WeekViewEvent from '~/components/attendance_schedules/events/WeekViewEvent.vue';
import MonthViewEvent from '~/components/attendance_schedules/events/MonthViewEvent.vue';
import MembersViewEvent from '~/components/attendance_schedules/events/MembersViewEvent.vue';
import ScheduleDialog from '~/components/attendance_schedules/dialogs/ScheduleDialog.vue';
import RemoveMultipleAttendanceSchedulesDialog from '~/components/attendance_schedules/dialogs/RemoveMultipleAttendanceSchedulesDialog.vue';
import TimeOffRequestDialog from '~/components/attendance_schedules/dialogs/TimeOffRequestDialog.vue';
import JobDialog from '~/components/attendance_schedules/dialogs/JobDialog.vue';
import SchedulesDropdown from './SchedulesDropdown.vue';
import { AttendanceSchedulesResource, TimeOffRequestsResource } from '~/resources';
import IcalendarDialog from '~/components/attendance_schedules/IcalendarDialog.vue';
import AppInfiniteScroll from '~/components/AppInfiniteScroll.vue';
import SchedulesFiltersViewToggles from '~/components/attendance_schedules/SchedulesFiltersViewToggles.vue';

import { toMomentWeekday, Formats } from '~/utils/time_utils';
import { savePageFiltersToLocalStorage } from '~/plugins/stateful_pages';

const FULLCALENDAR_KEY = window.FULLCALENDAR_KEY;
const EVENT_COMPONENT_MAP = {
  day: DayViewEvent,
  week: WeekViewEvent,
  month: MonthViewEvent,
};

export default {
  name: 'SchedulesIndex',
  components: {
    FullCalendar,
    SchedulesFilters,
    AttendanceShiftDialog,
    ScheduleDialog,
    TimeOffRequestDialog,
    TimeOffRequestActionDialog,
    JobDialog,
    HolidayDialog,
    IcalendarDialog,
    SchedulesDropdown,
    RemoveMultipleAttendanceSchedulesDialog,
    AppInfiniteScroll,
    HolidayWizard,
    TimeOffRequestSetupDialog,
    SchedulesFiltersViewToggles,
  },
  mixins: [InertiaFlashWatcher, InertiaLoadingWatcher],
  props: {
    events: {
      type: Array,
      required: true,
    },
    calendar_resources: {
      type: Object,
      required: true,
    },
    beta_dialog: {
      type: Object,
      required: true,
    },
    is_owner_manager: {
      type: Boolean,
      required: true,
    },
    clients: {
      type: Array,
      required: true,
    },
    visits_report_url: {
      type: String,
      required: true,
    },
    organization: {
      type: Object,
      required: true,
    },
    start_date: {
      type: String,
      required: true,
    },
    stop_date: {
      type: String,
      required: true,
    },
    time_zones: {
      type: Array,
      required: true,
    },
    time_zone: {
      type: Object,
      required: true,
    },
    active_view: {
      type: String,
      required: true,
      validator: (value) => ['month', 'week', 'day'].includes(value),
    },
    events_filter: {
      type: Array,
      default() {
        return [];
      },
    },
    enabled_event_types: {
      type: Array,
      required: true,
    },
    filtered_team_ids: {
      type: Array,
      default() {
        return [];
      },
    },
    filtered_user_ids: {
      type: Array,
      default() {
        return [];
      },
    },
    select_all: {
      type: String,
      default: '',
    },
    teams_enabled: {
      type: Boolean,
      required: true,
    },
    user_permissions: {
      type: Object,
      required: true,
    },
    subtype: {
      type: String,
      default: 'time',
      validator: (value) => ['time', 'members'].includes(value),
    },
    promotions: {
      type: Array,
      default: () => [],
    },
    country_list: {
      type: Array,
      required: true,
      validator: (value) => value.length > 0,
    },
    current_user_dropdown_object: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      IMAGE_EMPTY_BOX,

      fullcalendarLoaded: false,
      fullcalendarPlugins: [],
      filters: null,
      showNewScheduleDialog: false,
      showDeleteSchedulesDialog: false,
      scheduleDateRange: null,
      showIcalendarDialog: false,
      showHolidayWizardDialog: false,
      showTimeOffDialog: false,
      newScheduleDate: moment(),
      showEditScheduleDialog: false,
      editingAttendanceSchedule: null,
      currentEvent: null,
      selectedUserId: null,
      showAttendanceShiftDialog: false,
      showTimeOffRequestDialog: false,
      showJobDialog: false,
      showHolidayDialog: false,
      loading: false,
      appendData: false,
      unscheduledToggle: true,
      // FC event's `end` propery is exclusive of date, so in order to display correctly
      // we need to add 1 day
      allEvents: this.fullCalendarEvents(this.events),
      viewOptions: Object.freeze([
        {
          id: 'month',
          text: 'Month',
        },
        {
          id: 'week',
          text: 'Week',
        },
        {
          id: 'day',
          text: 'Day',
        },
      ]),
      weekViewOptions: Object.freeze([
        {
          id: 'members',
          text: 'Members',
        },
        {
          id: 'time',
          text: 'Time',
        },
      ]),
      allResources: this.calendar_resources,
      page: 1,
    };
  },
  computed: {
    ...mapInertiaProps({
      modalDialogs: 'modal_dialogs',
    }),
    hasMembersFilter() {
      return this.select_all?.includes('users') || !!this.filtered_user_ids?.length;
    },
    hasTeamsFilter() {
      return !!this.filtered_team_ids?.length;
    },
    redirectPath() {
      return location.pathname + location.search;
    },
    users() {
      return this.allResources.users || [];
    },
    defaultFilters() {
      return {
        start_date: this.start_date,
        stop_date: this.stop_date,
        events_filter: this.events_filter,
        time_zone: this.time_zone.for,
        active_view: this.active_view,
        subtype: this.subtype,
        filtered_user_ids: this.filtered_user_ids || this.users.map((user) => user.id),
        filtered_team_ids: this.filtered_team_ids,
        select_all: this.select_all,
        scheduled: this.unscheduledToggle ? 0 : 1,
      };
    },
    calendarOptions() {
      return {
        plugins: this.fullcalendarPlugins,
        initialView: this.fetchInitialView,
        now: moment().tz(this.time_zone.for).format(),
        showNonCurrentDates: false,
        editable: false,
        selectable: true,
        selectMirror: true,
        dayMaxEvents: 2,
        weekends: true,
        timeZone: this.time_zone.for,
        headerToolbar: { left: '', end: '' },
        initialDate: this.start_date,
        resources: this.users,
        resourceOrder: 'title',
        stickyHeaderDates: true,
        scrollTime: '07:00',
        firstDay: toMomentWeekday(this.organization.start_week_on),
        resourceAreaWidth: '18%',
        height: this.calendarHeight,
        schedulerLicenseKey: FULLCALENDAR_KEY,
        views: {
          resourceTimelineWeek: {
            slotDuration: { days: 1 },
            type: 'resourceTimeline',
            slotLabelFormat: {
              meridiem: 'short',
            },
            eventMaxStack: 2,
          },
        },
        listDayFormat: {
          month: 'short',
          day: '2-digit',
          weekday: 'short',
        },
        listDaySideFormat: false,
        slotLabelFormat: (object) => {
          return moment(object.date.marker).utc().format(Formats.time.hr_meridiem);
        },
        allDayText: 'All day',
        filterResourcesWithEvents: !this.unscheduledToggle,
        select: (e) => this.handleDateSelect(e),
        eventClick: (e) => this.handleEventClick(e),
        eventsSet: (e) => this.handleEventsSet(e),
        eventAdd: (e) => this.handleAdd(e),
        eventChange: (e) => this.handleChange(e),
        eventRemove: (e) => this.handleRemove(e),
        selectAllow: () => this.user_permissions.schedules.bulk_manage,
      };
    },
    fetchInitialView() {
      if (this.active_view === 'week' && this.subtype === 'members') {
        return 'resourceTimelineWeek';
      }

      return {
        day: 'listWeek',
        week: 'timeGridWeek',
        month: 'dayGridMonth',
      }[this.active_view];
    },
    onDayView() {
      return this.active_view === 'day';
    },
    onMonthView() {
      return this.active_view === 'month';
    },
    onWeekView() {
      return this.active_view === 'week' || this.active_view === 'members';
    },
    onMembersView() {
      return this.active_view === 'week' && this.subtype === 'members';
    },
    eventComponent() {
      if (this.onMembersView) {
        return MembersViewEvent;
      }

      return EVENT_COMPONENT_MAP[this.active_view];
    },
    // This is needed because of the preserveState: true, otherwise the calendar header
    // is not updated when we change the dates
    calendarKey() {
      return this.active_view + this.start_date + this.stop_date + this.subtype;
    },
    eventTypeMap() {
      return {
        Shift: {
          dialog: 'showAttendanceShiftDialog',
          attributes: 'shiftAttributes',
        },
        'Time off request': {
          dialog: 'showTimeOffRequestDialog',
          attributes: 'timeOffRequestAttributes',
        },
        Job: {
          dialog: 'showJobDialog',
          attributes: 'jobAttributes',
        },
        Holiday: {
          dialog: 'showHolidayDialog',
          attributes: 'holidayAttributes',
        },
      };
    },
    moreUsersToLoad() {
      return this.page < this.pageCount;
    },
    // On the members view hide the calendar's scroll, to use only the page one, on the others view, leave this
    // unset, so it will use aspectRatio.
    // @see https://fullcalendar.io/docs/aspectRatio
    calendarHeight() {
      return this.onMonthView ? null : 'auto';
    },
    pageCount() {
      return this.calendar_resources.page_count;
    },
    showMoreLink() {
      return this.moreUsersToLoad && this.onMembersView;
    },
  },
  watch: {
    fullcalendarLoaded(loaded) {
      if (loaded && this.$refs.loadMoreTrigger) {
        this.$nextTick(() => {
          this.intersectionObserver.observe(this.$refs.loadMoreTrigger);
        });
      }
    },
    events: function () {
      if (this.appendData) {
        const nonUniqueEvents = [...this.allEvents, ...this.fullCalendarEvents(this.events)];

        // Unique events
        this.allEvents = [...new Map(nonUniqueEvents.map((item) => [`${item.id}-${item.resourceId}`, item])).values()];
      } else {
        this.allEvents = this.fullCalendarEvents(this.events);
        if (this.page !== 1) {
          this.updateFilters([{ key: 'page', value: 1 }]);
        }
      }
    },
    calendar_resources: function () {
      if (this.appendData) {
        // toRaw + shallow copy: `allResources` is reactive data, so its users are Vue 3 Proxies.
        // Writing them into the Inertia prop makes the next history.replaceState throw
        // DataCloneError (structured clone can't copy Proxies) and every navigation dies.
        const users = [...toRaw(this.allResources).users, ...this.calendar_resources.users];

        this.allResources = { ...this.calendar_resources };
        this.allResources.users = users;
      } else {
        this.allResources = this.calendar_resources;
      }
    },
  },
  mounted() {
    // Listen for the pageshow event
    window.addEventListener('pageshow', (event) => {
      // Only emit the event if the page is not loaded from the cache
      if (!event.persisted) {
        window.BannersEventEmitter.emit('promotion-banners:init', this.promotions);
      }
    });
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        const [loadMoreButton] = entries;

        if (loadMoreButton?.isIntersecting && !this.loading) {
          this.loadMoreData();
        }
      },
      { rootMargin: '0px 0px 100px 0px' },
    );
  },
  beforeUnmount() {
    this.intersectionObserver.disconnect();
  },
  async created() {
    await this.loadCalendarPlugins();
  },
  methods: {
    async loadCalendarPlugins() {
      const plugins = await Promise.all([
        import('@fullcalendar/list').then((module) => module.default),
        import('@fullcalendar/daygrid').then((module) => module.default),
        import('@fullcalendar/timegrid').then((module) => module.default),
        import('@fullcalendar/interaction').then((module) => module.default),
        import('@fullcalendar/resource-timeline').then((module) => module.default),
        import('@fullcalendar/moment-timezone').then((module) => module.default),
      ]);

      this.fullcalendarPlugins = plugins;
      this.fullcalendarLoaded = true;
    },
    canAddShiftFor(userID) {
      return this.calendar_resources.can_manage || this.calendar_resources.managed_users.includes(userID);
    },
    handleDateSelect(selectInfo) {
      const userID = parseInt(selectInfo.resource.id);

      if (!this.canAddShiftFor(userID)) {
        return;
      }

      if (selectInfo.allDay) {
        this.scheduleDateRange = this.defaultScheduleDateRange(selectInfo.startStr);
      } else {
        this.scheduleDateRange = {
          start: moment.tz(selectInfo.startStr, this.time_zone.for),
          stop: moment.tz(selectInfo.endStr, this.time_zone.for),
        };
      }
      this.selectedUserId = selectInfo.resource ? userID : null;

      this.showNewScheduleDialog = true;
    },
    async handleEventClick({ event, jsEvent }) {
      if (this.loading) {
        return;
      }
      const extendedProps = {
        ...event.extendedProps,
        startStr: event.startStr,
        endStr: event.extendedProps.realEnd,
      };
      const eventType = extendedProps.type;
      const eventId = eventType === 'Job' ? extendedProps.jobId : event.id;
      const params = { time_zone: this.time_zone.for };
      let response = {};

      try {
        if (eventType === 'Time off request') {
          extendedProps.timeOffRequestAttributes = { id: Number(eventId) };
        } else {
          response = await this.eventDetails(eventId, eventType, params);
          extendedProps[this.eventTypeMap[eventType].attributes] = response;
        }
        this.currentEvent = extendedProps;
        this.toggleScheduleDialog(this.eventTypeMap[eventType].dialog, true);
      } catch (_err) {
        throw new Error(`Unable to fetch event details in Schedules`);
      }
    },
    handleEventsSet(events) {
      this.groupHolidaysAsOneEvent();
      this.groupJobsAsOneEvent();
    },
    handleAdd(e) {
      console.log(e);
    },
    handleChange(e) {
      console.log(e);
    },
    handleRemove(e) {
      console.log(e);
    },
    updateFilters(newFilterValues, options = {}) {
      const { origin, pathname } = window.location;
      const url = new URL(pathname, origin);
      const only = [
        ...newFilterValues.map(({ key }) => key),
        'events',
        'flash',
        'calendar_resources',
        'attendanceIcalendarURL',

        'select_all',
        'filtered_user_ids',
        'filtered_team_ids',
      ];

      // When we update the filters we should reset the page param so we don't have an inconsistent state.
      this.page = 1;

      url.search = this.getSearchParams(newFilterValues);
      const urlString = url.toString();

      savePageFiltersToLocalStorage({
        organizationId: this.organization?.id,
        userId: this.current_user_dropdown_object?.id,
        route: 'schedules',
        exceptParams: ['start_date', 'stop_date'],
        urlString,
      });

      this.$inertia.visit(urlString, {
        only,
        preserveState: true,
        ...options,
      });
    },
    getSearchParams(newFilterValues) {
      const newFilters = {
        ...this.defaultFilters,
        // Overwrite the default filters with the new filter key & value
        ...Object.fromEntries(newFilterValues.map(({ key, value }) => [key, value])),
      };

      // Force `filtered_user_ids` & `filtered_team_ids` to be string
      ['filtered_user_ids', 'filtered_team_ids'].forEach((key) => {
        if (Array.isArray(newFilters[key])) {
          newFilters[key] = newFilters[key].join('-');
        }
      });

      return Object.entries(newFilters)
        .reduce((params, [key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((val) => {
              if (val) {
                params.append(`${key}[]`, val);
              }
            });
          } else {
            if (value) {
              params.set(key, value);
            }
          }

          return params;
        }, new URLSearchParams())
        .toString();
    },
    toggleScheduleDialog(attribute, value) {
      this[attribute] = value;
    },
    setLoading(value) {
      this.loading = value;
    },
    closeScheduleDialog() {
      this.toggleScheduleDialog('showNewScheduleDialog', false);
      this.selectedUserId = null;
    },
    closeDialogs() {
      this.currentEvent = null;
      this.showAttendanceShiftDialog = false;
      this.showTimeOffRequestDialog = false;
      this.showJobDialog = false;
      this.showHolidayDialog = false;
    },
    formattedDay(date) {
      return moment(date).tz(this.time_zone.for).format(Formats.date.shortDay);
    },
    formattedDayName(date) {
      const formatTimeZone = this.onMonthView ? 'UTC' : this.time_zone.for;

      return moment(date).tz(formatTimeZone).format(Formats.date.shortDayName);
    },
    formattedMonth(date) {
      return moment(date).tz(this.time_zone.for).format('MMM');
    },
    openDialog(dialog) {
      switch (dialog) {
        case 'shifts': {
          const date = moment().tz(this.time_zone.for).format('YYYY-MM-DD');

          this.scheduleDateRange = this.defaultScheduleDateRange(date);

          this.toggleScheduleDialog('showNewScheduleDialog', true);
          break;
        }

        case 'delete_shifts': {
          this.toggleScheduleDialog('showDeleteSchedulesDialog', true);
          break;
        }

        case 'icalendar': {
          this.toggleScheduleDialog('showIcalendarDialog', true);
          break;
        }

        case 'holidays':
          this.toggleScheduleDialog('showHolidayWizardDialog', true);

          break;
        case 'time_off_request': {
          this.toggleScheduleDialog('showTimeOffDialog', true);

          break;
        }
      }
    },
    fcDate(date) {
      return moment.tz(date, this.time_zone.for).format();
    },
    fullCalendarEvents(events) {
      return events.map((event) => {
        let end = this.fcDate(event.end);
        // We store the real end value of the event here in case
        // it needs to be changed later due to rendering design choices
        const realEnd = end;

        // A design choice was made to show shifts that start on one day and end on another (Ex. 4pm-1am shifts)
        // only on the day they started. For that, we need to change the end date of the fullCalendar event.
        // We later indicate that the shift spills over to the next day through an icon.
        // The only exception to this is when viewing events on the Week - Time view,
        // as they need to be shown in scale to time
        if (event.type === 'Shift' && !this.onWeekTimeView()) {
          end = moment.tz(event.end, this.time_zone.for).isAfter(moment.tz(event.start, this.time_zone.for), 'day')
            ? moment.tz(event.start, this.time_zone.for).endOf('day').format()
            : event.end;
        }

        if (event.allDay) {
          end = moment.tz(event.end, this.time_zone.for).add(1, 'day').format();
        }

        return {
          ...event,
          start: this.fcDate(event.start),
          end,
          realEnd,
        };
      });
    },
    eventDetails(eventId, eventType, params) {
      if (eventType === 'Time off request') {
        return TimeOffRequestsResource.show(
          { id: eventId },
          { current_page_title: document.title, current_url: document.URL },
          { abortReload: true },
        );
      }

      return AttendanceSchedulesResource.eventDetails(
        { organizationId: this.organization.id, eventId, eventType },
        params,
      );
    },
    showUnscheduled() {
      this.unscheduledToggle = !this.unscheduledToggle;
      this.updateFilters([]);
    },
    defaultScheduleDateRange(date) {
      return {
        start: moment.utc(date).set({ hour: 9, minute: 0 }),
        stop: moment.utc(date).set({ hour: 17, minute: 0 }),
      };
    },
    loadMoreData() {
      if (!this.onMembersView || this.loading || !this.moreUsersToLoad) {
        return;
      }
      this.appendData = true;
      this.loading = true;

      const { origin, pathname } = window.location;
      const url = new URL(pathname, origin);
      const nextPage = this.page + 1;

      url.search = this.getSearchParams([{ key: 'page', value: nextPage }]);

      this.$inertia.get(
        url.toString(),
        {},
        {
          only: ['events', 'calendar_resources'],
          preserveState: true,
          preserveScroll: true,
          onSuccess: () => {
            this.page = nextPage;
          },
          onError: () => {
            this.$appMessage('Something went wrong. Please try again.');
          },
          onFinish: () => {
            this.$nextTick(() => {
              this.appendData = false;
            });
            setTimeout(() => {
              this.loading = false;
            }, 10);
          },
        },
      );
    },
    /*
     * Groups multiple holiday events into one holiday event.
     */
    groupHolidaysAsOneEvent() {
      if (this.onMembersView) {
        return;
      }

      const holidays = {};

      this.allEvents.forEach((event) => {
        if (event.type === 'Holiday') {
          holidays[event.holidayId] = holidays[event.holidayId] || [];
          holidays[event.holidayId].push(event);
        }
      });

      Object.keys(holidays).forEach((holidayId) => {
        const holidaysArray = holidays[holidayId];
        const firstHoliday = holidaysArray[0];

        // This means we do not need to group this holiday
        if (holidaysArray.length <= 1) {
          return;
        }

        firstHoliday.members = holidaysArray.map((e) => ({ avatar: e.avatar }));
        holidaysArray.slice(1, holidaysArray.length).forEach((holiday) => {
          this.allEvents.splice(this.allEvents.indexOf(holiday), 1);
        });
      });
    },
    groupJobsAsOneEvent() {
      if (this.onMembersView) {
        return;
      }

      const result = [];
      const seenJobs = new Map();

      for (const event of this.allEvents) {
        if (event.type.toLowerCase() !== 'job') {
          result.push(event);
          continue;
        }

        if (seenJobs.has(event.jobId)) {
          continue;
        }

        seenJobs.set(event.jobId, true);
        result.push(event);
      }

      // Only changes the events if we grouped some jobs on the run,
      // otherwise we can get in an infinite loop.
      if (result.length !== this.allEvents.length) {
        this.allEvents = result;
      }
    },
    onWeekTimeView() {
      return this.active_view === 'week' && this.subtype === 'time';
    },
  },
};
</script>
